import type { ApplicationDocumentDescriptor } from "./application-route"
import { useDocumentInstance, useDocumentInstanceOperations } from "./document-instance"

export interface ApplicationDocument<
  TParams = Readonly<Record<string, string>>,
  TSearch = unknown,
  TLoaderData = unknown,
> extends ApplicationDocumentDescriptor<TParams, TSearch, TLoaderData> {
  readonly instanceId: string
  readonly active: boolean
  activate(): void
  close(): Promise<boolean>
}

/**
 * Returns the routed application document hosting the current component.
 * Application document components can use this instead of handling workspace ids or URLs.
 */
export function useApplicationDocument<
  TParams = Readonly<Record<string, string>>,
  TSearch = unknown,
  TLoaderData = unknown,
>(): ApplicationDocument<TParams, TSearch, TLoaderData> {
  const instance = useDocumentInstance<ApplicationDocumentDescriptor<TParams, TSearch, TLoaderData>>()
  const operations = useDocumentInstanceOperations()
  const descriptor = instance?.descriptor
  if (instance == null || descriptor == null || operations == null) {
    throw new Error("useApplicationDocument must be used inside a routed application document.")
  }

  return {
    ...descriptor,
    instanceId: instance.id,
    active: instance.active,
    activate: operations.activate,
    close: operations.close,
  }
}
